/**
 * Liveness Controller
 *
 * Orchestrates the liveness detection flow in the verification UI.
 * Handles action sequencing, failure modals, retries, and demo mode.
 */

import { verificationState, LivenessAction } from '../state/verification-state';
import { i18n } from '../../i18n';

// Re-export type for convenience
export type { LivenessAction };

/**
 * Action display text configuration.
 */
export interface ActionText {
  title: string;
  hint: string;
}

/**
 * Map of LivenessAction to i18n liveness key names.
 */
const ACTION_I18N_KEY_MAP: Record<
  LivenessAction,
  keyof typeof i18n extends never ? string : string
> = {
  BLINK: 'blink',
  SMILE: 'smile',
  LOOK_LEFT: 'lookLeft',
  LOOK_RIGHT: 'lookRight',
  OPEN_MOUTH: 'openMouth',
  LOOK_UP: 'lookUp',
  LOOK_DOWN: 'lookDown',
  RAISE_BROWS: 'raiseBrows',
};

/**
 * Gets localized action text for a liveness action.
 */
function getLocalizedActionText(action: LivenessAction): ActionText {
  const translations = i18n.getTranslations();
  const key = ACTION_I18N_KEY_MAP[action] as keyof typeof translations.liveness;
  const actionText = translations.liveness[key];

  // Handle the improveLighting case which has the same structure
  if (
    actionText &&
    typeof actionText === 'object' &&
    'title' in actionText &&
    'hint' in actionText
  ) {
    return {
      title: actionText.title,
      hint: actionText.hint,
    };
  }

  // Fallback
  return { title: action, hint: '' };
}

/**
 * Gets localized "improve lighting" text.
 */
function getLocalizedLightingText(): ActionText {
  const translations = i18n.getTranslations();
  return {
    title: translations.liveness.improveLighting.title,
    hint: translations.liveness.improveLighting.hint,
  };
}

/**
 * Map SDK action names to backend field names.
 */
export const ACTION_NAME_MAP: Record<LivenessAction, string> = {
  BLINK: 'blink',
  LOOK_LEFT: 'turn_left',
  LOOK_RIGHT: 'turn_right',
  SMILE: 'smile',
  OPEN_MOUTH: 'open_mouth',
  LOOK_UP: 'look_up',
  LOOK_DOWN: 'look_down',
  RAISE_BROWS: 'raise_brows',
};

/**
 * Gets all action text (localized).
 * @deprecated Use getLocalizedActionText instead
 */
export function getActionTextMap(): Record<LivenessAction, ActionText> {
  const result = {} as Record<LivenessAction, ActionText>;
  for (const action of Object.keys(ACTION_I18N_KEY_MAP) as LivenessAction[]) {
    result[action] = getLocalizedActionText(action);
  }
  return result;
}

/**
 * Localized action text for each liveness action.
 * @deprecated Use getLocalizedActionText(action) instead
 */
export const ACTION_TEXT = new Proxy({} as Record<LivenessAction, ActionText>, {
  get(_target, prop: string) {
    return getLocalizedActionText(prop as LivenessAction);
  },
});

/**
 * Result from a single liveness action.
 */
export interface ActionResult {
  action: LivenessAction;
  ok: boolean;
}

/**
 * Complete liveness flow result.
 */
export interface LivenessResult {
  passed: boolean;
  actions: ActionResult[];
}

/**
 * Events emitted by the controller.
 */
export interface LivenessControllerEvents {
  onStepStart?: (action: LivenessAction, stepIndex: number) => void;
  onStepComplete?: (action: LivenessAction, stepIndex: number) => void;
  onFlowComplete?: (result: LivenessResult) => void;
  onFlowFailed?: (error?: Error) => void;
  onUIUpdate?: (title: string, hint: string, stepIndex: number) => void;
  onShowFailureModal?: () => void;
  onHideFailureModal?: () => void;
  onLightingBlocked?: () => void;
}

/**
 * Configuration for the liveness controller.
 */
export interface LivenessControllerConfig {
  /** Default actions if not provided by SDK. Default: ['BLINK', 'SMILE'] */
  defaultActions: LivenessAction[];
  /** Demo mode delay between steps in ms. Default: 2000 */
  demoStepDelayMs: number;
  /** Lighting check retry interval in ms. Default: 500 */
  lightingRetryMs: number;
}

/**
 * Default configuration values.
 */
const DEFAULT_CONFIG: LivenessControllerConfig = {
  defaultActions: ['BLINK', 'SMILE'],
  demoStepDelayMs: 2000,
  lightingRetryMs: 500,
};

/**
 * LivenessController orchestrates the liveness detection flow.
 *
 * Features:
 * - Manages action sequence (BLINK, SMILE, etc.)
 * - Prevents duplicate flow starts with guard flag
 * - Handles failure modal and retry logic
 * - Demo mode simulation
 * - Progress tracking
 */
export class LivenessController {
  private config: LivenessControllerConfig;
  private events: LivenessControllerEvents;

  // Flow state
  private isFlowActive = false;
  private currentStep = 0;
  private actions: LivenessAction[] = [];
  private results: ActionResult[] = [];

  // External callbacks
  private isActiveScreen: () => boolean = () => true;
  private isLightingAcceptable: () => boolean = () => true;
  private sdkController: unknown = null;

  constructor(
    config: Partial<LivenessControllerConfig> = {},
    events: LivenessControllerEvents = {}
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.events = events;
    this.actions = [...this.config.defaultActions];
  }

  /**
   * Sets a callback to check if we're on the liveness screen.
   */
  setActiveScreenCheck(check: () => boolean): void {
    this.isActiveScreen = check;
  }

  /**
   * Sets a callback to check if lighting is acceptable.
   */
  setLightingCheck(check: () => boolean): void {
    this.isLightingAcceptable = check;
  }

  /**
   * Sets the SDK controller for real liveness detection.
   */
  setSdkController(controller: unknown): void {
    this.sdkController = controller;
  }

  /**
   * Sets the actions to perform.
   */
  setActions(actions: LivenessAction[]): void {
    this.actions = [...actions];
    this.updateVerificationState();
  }

  /**
   * Gets the current actions list.
   */
  getActions(): LivenessAction[] {
    return [...this.actions];
  }

  /**
   * Starts the liveness flow.
   *
   * @returns true if flow started, false if already running
   */
  async start(): Promise<boolean> {
    console.log('[LivenessController] Starting liveness flow...');

    // Guard: prevent duplicate flows
    if (this.isFlowActive) {
      console.log('[LivenessController] Flow already active, ignoring duplicate call');
      return false;
    }

    this.isFlowActive = true;
    this.currentStep = 0;
    this.results = [];

    this.updateVerificationState();

    // If SDK controller is available, use it
    if (this.sdkController && typeof (this.sdkController as any).runLivenessFlow === 'function') {
      try {
        const success = await (this.sdkController as any).runLivenessFlow();

        if (success) {
          console.log('[LivenessController] SDK flow completed successfully');

          // Get results from SDK
          const livenessResult =
            typeof (this.sdkController as any).getLivenessResult === 'function'
              ? (this.sdkController as any).getLivenessResult()
              : null;

          // Build actions map for backend
          const result = this.buildResultFromSdk(livenessResult);

          this.isFlowActive = false;
          this.events.onFlowComplete?.(result);
          return true;
        } else {
          console.log('[LivenessController] SDK flow failed');
          this.isFlowActive = false;
          this.events.onFlowFailed?.();
          this.events.onShowFailureModal?.();
          return false;
        }
      } catch (error) {
        console.error('[LivenessController] SDK flow error:', error);
        this.isFlowActive = false;
        this.events.onFlowFailed?.(error instanceof Error ? error : new Error(String(error)));
        this.events.onShowFailureModal?.();
        return false;
      }
    } else {
      // Demo mode - simulate liveness steps
      this.runDemoFlow();
      return true;
    }
  }

  /**
   * Builds result object from SDK liveness result.
   */
  private buildResultFromSdk(livenessResult: any): LivenessResult {
    const actions: Record<string, boolean> = {};

    if (livenessResult?.actions) {
      for (const actionResult of livenessResult.actions) {
        const backendFieldName = ACTION_NAME_MAP[actionResult.action as LivenessAction];
        if (backendFieldName) {
          actions[backendFieldName] = actionResult.ok;
        }
      }
    }

    console.log('[LivenessController] Built result:', {
      passed: livenessResult?.passed ?? true,
      actions,
    });

    return {
      passed: livenessResult?.passed ?? true,
      actions: livenessResult?.actions ?? [],
    };
  }

  /**
   * Runs the demo mode liveness flow.
   */
  private runDemoFlow(): void {
    console.log('[LivenessController] Running demo simulation...');
    this.runNextDemoStep();
  }

  /**
   * Runs the next demo step.
   */
  private runNextDemoStep(): void {
    if (this.currentStep >= this.actions.length) {
      // All steps completed
      console.log('[LivenessController] Demo completed');

      const result: LivenessResult = {
        passed: true,
        actions: this.actions.map((action) => ({ action, ok: true })),
      };

      this.isFlowActive = false;
      this.events.onFlowComplete?.(result);
      return;
    }

    // Check lighting
    if (!this.isLightingAcceptable()) {
      console.log('[LivenessController] Waiting for better lighting...');
      this.events.onLightingBlocked?.();
      const lightingText = getLocalizedLightingText();
      this.events.onUIUpdate?.(lightingText.title, lightingText.hint, this.currentStep);

      // Retry after delay
      setTimeout(() => {
        if (this.isActiveScreen() && this.isFlowActive) {
          this.runNextDemoStep();
        }
      }, this.config.lightingRetryMs);
      return;
    }

    const action = this.actions[this.currentStep];
    const actionText = getLocalizedActionText(action);

    this.events.onStepStart?.(action, this.currentStep);
    this.events.onUIUpdate?.(actionText.title, actionText.hint, this.currentStep);

    // Update verification state
    verificationState.update('liveness', {
      currentStep: this.currentStep,
    });

    // Simulate action detection after delay
    setTimeout(() => {
      if (!this.isActiveScreen() || !this.isFlowActive) {
        return;
      }

      // Mark step as complete
      this.results.push({ action, ok: true });
      this.events.onStepComplete?.(action, this.currentStep);

      this.currentStep++;
      this.runNextDemoStep();
    }, this.config.demoStepDelayMs);
  }

  /**
   * Handles retry from failure modal.
   */
  retry(): void {
    console.log('[LivenessController] User chose to retry liveness');

    this.events.onHideFailureModal?.();
    this.reset();

    // Reset SDK if available
    if (this.sdkController && typeof (this.sdkController as any).resetLiveness === 'function') {
      (this.sdkController as any).resetLiveness();
    }

    // Restart flow
    this.start();
  }

  /**
   * Handles start over from failure modal.
   * Returns state needed for full reset.
   */
  startOver(): void {
    console.log('[LivenessController] User chose to start over');

    this.events.onHideFailureModal?.();

    // Reset SDK if available
    if (this.sdkController && typeof (this.sdkController as any).resetLiveness === 'function') {
      (this.sdkController as any).resetLiveness();
    }
    if (this.sdkController && typeof (this.sdkController as any).cleanup === 'function') {
      (this.sdkController as any).cleanup();
    }

    this.reset();
  }

  /**
   * Resets the controller state.
   */
  reset(): void {
    this.isFlowActive = false;
    this.currentStep = 0;
    this.results = [];

    this.updateVerificationState();
  }

  /**
   * Updates the verification state.
   */
  private updateVerificationState(): void {
    verificationState.update('liveness', {
      currentStep: this.currentStep,
      steps: this.actions,
    });
  }

  /**
   * Gets the current step index.
   */
  getCurrentStep(): number {
    return this.currentStep;
  }

  /**
   * Gets the total number of steps.
   */
  getTotalSteps(): number {
    return this.actions.length;
  }

  /**
   * Gets progress as a percentage.
   */
  getProgress(): number {
    if (this.actions.length === 0) return 0;
    return Math.round((this.currentStep / this.actions.length) * 100);
  }

  /**
   * Checks if the flow is currently active.
   */
  isActive(): boolean {
    return this.isFlowActive;
  }

  /**
   * Gets the action display text (localized).
   */
  getActionText(action: LivenessAction): ActionText {
    return getLocalizedActionText(action);
  }

  /**
   * Gets the backend field name for an action.
   */
  getBackendFieldName(action: LivenessAction): string {
    return ACTION_NAME_MAP[action];
  }

  /**
   * Disposes of the controller.
   */
  dispose(): void {
    this.isFlowActive = false;
    this.currentStep = 0;
    this.results = [];
    this.sdkController = null;
  }
}

/**
 * Singleton instance for convenience.
 */
export const livenessController = new LivenessController();
